import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  Automation,
  AutomationExecution,
  AutomationExecutionListQuery,
  AutomationListItem,
  AutomationListQuery,
  CollectionMeta,
  CreateAutomationInput,
  RunAutomationInput,
  RunAutomationResult,
  UpdateAutomationInput,
} from '@neuroedge/shared';
import { apiClient, ApiClientError } from './client';

const KEY = ['automations'] as const;

export interface AutomationListPage {
  items: AutomationListItem[];
  meta: CollectionMeta;
}

export interface AutomationExecutionListPage {
  items: AutomationExecution[];
  meta: CollectionMeta;
}

function toQuery(q: AutomationListQuery): Record<string, string | number | undefined> {
  return { page: q.page, pageSize: q.pageSize, status: q.status, channelId: q.channelId };
}

export async function listAutomations(q: AutomationListQuery = {}): Promise<AutomationListPage> {
  const res = await apiClient.getCollection<AutomationListItem>('/automations', {
    query: toQuery(q),
  });
  return { items: res.data, meta: res.meta };
}
export const getAutomation = (id: string) => apiClient.get<Automation>(`/automations/${id}`);
export const createAutomation = (input: CreateAutomationInput) =>
  apiClient.post<Automation>('/automations', input);
export const updateAutomation = (id: string, input: UpdateAutomationInput) =>
  apiClient.patch<Automation>(`/automations/${id}`, input);
export const deleteAutomation = (id: string) => apiClient.delete<void>(`/automations/${id}`);
export const activateAutomation = (id: string) =>
  apiClient.post<Automation>(`/automations/${id}/activate`);
export const deactivateAutomation = (id: string) =>
  apiClient.post<Automation>(`/automations/${id}/deactivate`);
export const runAutomation = (id: string, input: RunAutomationInput = {}) =>
  apiClient.post<RunAutomationResult>(`/automations/${id}/run`, input);

export async function listAutomationExecutions(
  automationId: string,
  q: AutomationExecutionListQuery = {},
): Promise<AutomationExecutionListPage> {
  const res = await apiClient.getCollection<AutomationExecution>(
    `/automations/${automationId}/executions`,
    { query: { page: q.page, pageSize: q.pageSize } },
  );
  return { items: res.data, meta: res.meta };
}

export function useAutomations(query: AutomationListQuery = {}) {
  return useQuery<AutomationListPage, ApiClientError>({
    queryKey: [...KEY, 'list', query],
    queryFn: () => listAutomations(query),
  });
}

export function useAutomation(id: string | undefined) {
  return useQuery<Automation, ApiClientError>({
    queryKey: [...KEY, 'detail', id],
    queryFn: () => getAutomation(id!),
    enabled: Boolean(id),
  });
}

export function useCreateAutomation() {
  const qc = useQueryClient();
  return useMutation<Automation, ApiClientError, CreateAutomationInput>({
    mutationFn: createAutomation,
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useUpdateAutomation(id: string) {
  const qc = useQueryClient();
  return useMutation<Automation, ApiClientError, UpdateAutomationInput>({
    mutationFn: (input) => updateAutomation(id, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useDeleteAutomation() {
  const qc = useQueryClient();
  return useMutation<void, ApiClientError, string>({
    mutationFn: deleteAutomation,
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useActivateAutomation() {
  const qc = useQueryClient();
  return useMutation<Automation, ApiClientError, string>({
    mutationFn: activateAutomation,
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useDeactivateAutomation() {
  const qc = useQueryClient();
  return useMutation<Automation, ApiClientError, string>({
    mutationFn: deactivateAutomation,
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useAutomationExecutions(
  automationId: string | undefined,
  query: AutomationExecutionListQuery = {},
) {
  return useQuery<AutomationExecutionListPage, ApiClientError>({
    queryKey: [...KEY, 'executions', automationId, query],
    queryFn: () => listAutomationExecutions(automationId!, query),
    enabled: Boolean(automationId),
  });
}
